/**
 * Dashboards people describe in their own words: the assistant lays each one out from the metric catalogue, and the
 * figures are computed from the ledger on every view. Shared with the whole company; accountants and up build and
 * arrange them (drag, resize, change the period or the chart, add any metric by hand), and anyone can present,
 * print or download them. One dashboard can be pinned to the Command Center.
 */
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api, useAction, useApi } from '../lib/api';
import { date, exponent, money, num, pct, period, relative, toMajor } from '../lib/format';
import { useSession } from '../lib/session';
import { toast } from '../lib/toast';
import type { AiStatus, CatalogueMetric, Dashboard, DashboardCatalogue, DashboardData, DashboardSummary, MetricFormat, MetricKind, Widget, WidgetData, WidgetSize } from '../lib/types';
import { BarChart, HBars, Legend, LineChart, Sparkline } from '../components/charts';
import { Icons } from '../components/icons';
import { Badge, Button, Card, cx, Empty, ErrorNote, Field, Input, Menu, Modal, PageHeader, Query, SectionTitle, Skeleton, Table, Td, Textarea, Th, Tr, type MenuEntry } from '../components/ui';

const EXAMPLES = [
  'A board view: cash, runway, MRR growth and our biggest customers',
  'Collections: who owes us, how late, and the aging',
  'Spend over the last 6 months, by vendor and by department',
];

/** Same cap as the server's MAX_WIDGETS. */
const MAX_WIDGETS = 16;

export function DashboardsPage() {
  const { can } = useSession();
  const list = useApi<DashboardSummary[]>('/api/dashboards');
  const catalogue = useApi<DashboardCatalogue>('/api/dashboards/catalogue');
  return (
    <>
      <PageHeader title="Dashboards" description="Describe the view you want and the AI assistant lays it out from your live books. Every dashboard is shared with the whole company." />
      {can('ACCOUNTANT') && catalogue.data && <Builder catalogue={catalogue.data} />}
      <Query q={list}>
        {(rows) =>
          rows.length ? (
            <>
              <SectionTitle>Company dashboards</SectionTitle>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {rows.map((d) => (
                  <Link key={d.id} to={`/dashboards/${d.id}`} className="group glass rounded-2xl p-4 transition-colors hover:bg-white/85">
                    <div className="flex items-start gap-3">
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-brand text-site-lime shadow-[inset_0_1px_0_rgb(255_255_255/0.15)]">
                        <Icons.Dashboard size={17} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="app-title truncate text-[15px] text-ink group-hover:text-brand">{d.title}</span>
                          {d.pinned && (
                            <Badge tone="brand" className="shrink-0">
                              Pinned
                            </Badge>
                          )}
                        </div>
                        {d.description && <p className="mt-0.5 line-clamp-2 text-[12.5px] text-muted">{d.description}</p>}
                      </div>
                    </div>
                    <div className="mt-3 text-[11.5px] text-muted">
                      {d.widgetCount} {d.widgetCount === 1 ? 'widget' : 'widgets'}
                      {d.createdByName && ` · by ${d.createdByName}`} · updated {relative(d.updatedAt)}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <Card>
              <Empty title="No dashboards yet" icon={<Icons.Dashboard size={28} />}>
                {can('ACCOUNTANT') ? 'Describe one above, or start from a template.' : 'Ask an accountant or admin in your company to build one.'}
              </Empty>
            </Card>
          )
        }
      </Query>
    </>
  );
}

/** The prompt box, and the templates (free: no assistant message). */
function Builder({ catalogue }: { catalogue: DashboardCatalogue }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const status = useApi<AiStatus>(catalogue.ai ? '/api/ai/status' : null);
  const build = useAction((body: { prompt: string } | { template: string }) => api<Dashboard>('/api/dashboards', { method: 'POST', body }));
  const go = (body: { prompt: string } | { template: string }) => build.mutate(body, { onSuccess: (d) => navigate(`/dashboards/${d.id}`, { state: { notes: d.notes ?? null } }) });
  const usage = status.data?.usage;
  const left = usage ? Math.max(0, usage.limit - usage.used) : null;
  const ready = prompt.trim().length >= 3 && left !== 0 && !build.isPending;
  const submit = () => ready && go({ prompt: prompt.trim() });
  const designing = build.isPending && !!build.variables && 'prompt' in build.variables;

  return (
    <Card className="mb-6">
      {catalogue.ai ? (
        <>
          <label htmlFor="dashboard-prompt" className="flex items-center gap-2 text-[14px] text-ink">
            <span className="grid size-7 place-items-center rounded-lg bg-ai-soft text-ai">
              <Icons.Sparkle size={15} />
            </span>
            <span className="app-title">Build a dashboard</span>
          </label>
          <Textarea
            id="dashboard-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
            }}
            maxLength={2000}
            disabled={build.isPending}
            placeholder="Describe it in your own words: which numbers, over what period, for which meeting."
            className="mt-3 min-h-20 w-full"
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {EXAMPLES.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setPrompt(e)}
                disabled={build.isPending}
                className="rounded-full border border-ink/[0.08] bg-white/60 px-2.5 py-1 text-[12px] text-ink-2 transition-colors hover:border-ai/40 hover:bg-ai-soft hover:text-ai"
              >
                {e}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-xl text-[12px] text-muted">
              {left === 0
                ? 'Your company has used all its assistant messages this month. Templates and editing by hand still work.'
                : `Uses 1 assistant message${usage ? ` (${left} of ${usage.limit} left this month)` : ''}. The assistant only chooses the figures; it never sees or changes your numbers.`}
            </p>
            <Button variant="ai" icon={<Icons.Sparkle size={14} />} loading={designing} disabled={!ready} onClick={submit}>
              {designing ? 'Designing…' : 'Build dashboard'}
            </Button>
          </div>
        </>
      ) : (
        <p className="text-[13px] text-muted">The assistant is off on this server, so new dashboards start from a template. Set an AI key on the server to describe your own.</p>
      )}
      <div className={cx(catalogue.ai && 'mt-4 border-t border-ink/[0.06] pt-4')}>
        <div className="app-eyebrow text-[10px] text-muted">{catalogue.ai ? 'Or start from a template (free)' : 'Templates'}</div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {catalogue.templates.map((t) => (
            <button
              key={t.key}
              type="button"
              disabled={build.isPending}
              onClick={() => go({ template: t.key })}
              className="rounded-xl border border-white/80 bg-white/55 px-3 py-2.5 text-left shadow-card transition-colors hover:bg-white/90 disabled:opacity-60"
            >
              <div className="flex items-center gap-1.5 text-[13px] font-medium text-ink">
                {t.title}
                {build.isPending && build.variables && 'template' in build.variables && build.variables.template === t.key && <span className="text-[11.5px] font-normal text-muted">Creating…</span>}
              </div>
              <div className="mt-0.5 text-[12px] text-muted">{t.description}</div>
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { can } = useSession();
  const canEdit = can('ACCOUNTANT');
  const path = `/api/dashboards/${id}`;
  const q = useApi<Dashboard>(path);
  const data = useApi<DashboardData>(`${path}/data`, { refetchInterval: 60_000 });
  const catalogue = useApi<DashboardCatalogue>(canEdit ? '/api/dashboards/catalogue' : null, { staleTime: 10 * 60_000 });
  const [notes, setNotes] = useState<string | null>((location.state as { notes?: string | null } | null)?.notes ?? null);
  const [revising, setRevising] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [adding, setAdding] = useState(false);
  const [presenting, setPresenting] = useState(false);
  // The last widget removed, so a slip of the mouse on a shared dashboard can be put back.
  const [removed, setRemoved] = useState<{ title: string; widgets: Widget[] } | null>(null);
  const patch = useAction((body: { title?: string; description?: string; widgets?: Widget[] }) => api<Dashboard>(path, { method: 'PATCH', body }));
  // Leave the page before the refresh that follows a delete, so it never shows "not found".
  const remove = useAction(() => api(path, { method: 'DELETE' }).then(() => navigate('/dashboards')), { success: 'Dashboard deleted' });
  const duplicate = useAction(() => api<Dashboard>(`${path}/duplicate`, { method: 'POST' }), { success: 'Copy created' });
  const pin = useAction((pinned: boolean) => api<Dashboard>(`${path}/pin`, { method: 'PUT', body: { pinned } }), { success: (d) => (d.pinned ? 'Pinned to the Command Center' : 'Taken off the Command Center') });

  /** A new layout: shown at once, then saved. A failed save puts the stored layout back. */
  const save = (widgets: Widget[], removedWidget?: Widget) => {
    const before = q.data!.widgets;
    qc.setQueryData<Dashboard>([path], (d) => d && { ...d, widgets });
    patch.mutate({ widgets }, { onSuccess: () => removedWidget && setRemoved({ title: removedWidget.title, widgets: before }), onError: () => void qc.invalidateQueries({ queryKey: [path] }) });
  };

  return (
    <Query q={q}>
      {(d) => {
        const more: MenuEntry[] = [
          ...(canEdit
            ? ([
                { label: 'Rename', icon: <Icons.Doc size={15} />, onSelect: () => setRenaming(true) },
                { label: 'Duplicate', icon: <Icons.Copy size={15} />, onSelect: () => duplicate.mutate(undefined, { onSuccess: (c) => navigate(`/dashboards/${c.id}`) }) },
                { label: d.pinned ? 'Unpin from Command Center' : 'Pin to Command Center', icon: <Icons.Pin size={15} />, onSelect: () => pin.mutate(!d.pinned) },
              ] satisfies MenuEntry[])
            : []),
          { label: 'Print or save as PDF', icon: <Icons.Printer size={15} />, onSelect: printSheet },
          ...(canEdit ? (['divider', { label: 'Delete dashboard', icon: <Icons.Trash size={15} />, danger: true, onSelect: () => confirm(`Delete “${d.title}” for everyone in the company?`) && remove.mutate() }] satisfies MenuEntry[]) : []),
        ];
        return (
          <div className="print-sheet">
            <PageHeader
              eyebrow={
                <Link to="/dashboards" className="hover:text-ink">
                  Dashboards
                </Link>
              }
              title={
                <span className="flex flex-wrap items-center gap-2.5">
                  {d.title}
                  {d.pinned && (
                    <Badge tone="brand" className="print:hidden">
                      <Icons.Pin size={12} /> On the Command Center
                    </Badge>
                  )}
                </span>
              }
              description={d.description || undefined}
              actions={
                <div className="flex flex-wrap items-center gap-2 print:hidden">
                  <Button icon={<Icons.Present size={15} />} onClick={() => setPresenting(true)}>
                    Present
                  </Button>
                  {canEdit && (
                    <>
                      <Button icon={<Icons.Plus size={15} />} disabled={d.widgets.length >= MAX_WIDGETS} title={d.widgets.length >= MAX_WIDGETS ? `A dashboard holds at most ${MAX_WIDGETS} widgets` : 'Add any figure by hand (free)'} onClick={() => setAdding(true)}>
                        Add widget
                      </Button>
                      <Button variant="ai" icon={<Icons.Sparkle size={14} />} onClick={() => setRevising((v) => !v)}>
                        Edit with AI
                      </Button>
                    </>
                  )}
                  <Menu
                    label="More actions"
                    items={more}
                    button="inline-flex size-8 items-center justify-center rounded-[10px] border border-line-strong/90 bg-white/80 text-ink-2 shadow-[0_1px_2px_rgb(27_26_24/0.05)] transition-colors hover:bg-white aria-expanded:bg-white"
                  >
                    <Icons.More size={16} />
                  </Menu>
                </div>
              }
            />
            {d.prompts.length > 0 && (
              <p className="-mt-3 mb-4 text-[12px] text-muted print:hidden">
                Built from “{d.prompts[0]}”{d.prompts.length > 1 && ` · edited with the assistant ${d.prompts.length - 1 === 1 ? 'once' : `${d.prompts.length - 1} times`}`}
              </p>
            )}
            {notes && <Notice onClose={() => setNotes(null)}>{notes}</Notice>}
            {revising && <Revise id={d.id} onDone={(n) => (setRevising(false), setNotes(n))} onCancel={() => setRevising(false)} />}
            {removed && (
              <div role="status" className="glass mb-4 flex items-center gap-3 rounded-xl px-3 py-2 text-[12.5px] text-ink-2 print:hidden">
                <span className="min-w-0 flex-1">Removed “{removed.title}” for everyone in the company.</span>
                <button type="button" disabled={patch.isPending} onClick={() => patch.mutate({ widgets: removed.widgets }, { onSuccess: () => setRemoved(null) })} className="font-medium text-brand hover:underline">
                  Undo
                </button>
                <button type="button" onClick={() => setRemoved(null)} aria-label="Dismiss" className="rounded p-0.5 text-faint hover:bg-ink/[0.05] hover:text-ink">
                  <Icons.X size={13} />
                </button>
              </div>
            )}
            <ErrorNote error={data.error} className="mb-4" />
            <DashboardGrid
              dashboard={d}
              data={data.data}
              edit={canEdit ? { metrics: catalogue.data?.metrics ?? [], onChange: save } : undefined}
            />
            {data.data && <p className="mt-4 text-[12px] text-muted">Figures as of {date(data.data.asOf)}, computed from the ledger each time this page loads.</p>}
            {renaming && <RenameModal dashboard={d} saving={patch.isPending} onSave={(body) => patch.mutate(body, { onSuccess: () => setRenaming(false) })} onClose={() => setRenaming(false)} />}
            {adding && catalogue.data && (
              <AddWidgetModal
                metrics={catalogue.data.metrics}
                onClose={() => setAdding(false)}
                onAdd={(m) => {
                  setAdding(false);
                  save([...d.widgets, newWidget(m)]);
                  // The new widget lands at the end of the grid.
                  setTimeout(() => document.querySelector('main')?.scrollTo({ top: 1e9, behavior: 'smooth' }), 300);
                }}
              />
            )}
            {presenting && <Present dashboard={d} data={data.data} onClose={() => setPresenting(false)} />}
          </div>
        );
      }}
    </Query>
  );
}

/**
 * Print the dashboard (or save it as a PDF from the print dialog) on a landscape sheet. The page is held at the
 * sheet's width first, so the charts measure themselves for paper before the browser lays it out.
 */
function printSheet() {
  const style = document.createElement('style');
  style.textContent = '@page { size: landscape; margin: 12mm; }';
  document.head.append(style);
  document.documentElement.dataset.printing = '';
  const done = () => {
    delete document.documentElement.dataset.printing;
    style.remove();
    window.removeEventListener('afterprint', done);
  };
  window.addEventListener('afterprint', done);
  requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(() => window.print(), 60)));
}

function Notice({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div role="status" className="mb-4 flex items-start gap-2 rounded-xl border border-ai/20 bg-ai-soft/90 px-3 py-2 text-[12.5px] text-ai print:hidden">
      <Icons.Sparkle size={14} className="mt-0.5 shrink-0" />
      <span className="min-w-0 flex-1">{children}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss" className="rounded p-0.5 hover:bg-ai/10">
        <Icons.X size={13} />
      </button>
    </div>
  );
}

function Revise({ id, onDone, onCancel }: { id: string; onDone: (notes: string | null) => void; onCancel: () => void }) {
  const [prompt, setPrompt] = useState('');
  const revise = useAction((p: string) => api<Dashboard>(`/api/dashboards/${id}/revise`, { method: 'POST', body: { prompt: p } }), { success: 'Dashboard updated' });
  const ready = prompt.trim().length >= 3 && !revise.isPending;
  const submit = () => ready && revise.mutate(prompt.trim(), { onSuccess: (d) => onDone(d.notes ?? null) });
  return (
    <Card className="mb-4 print:hidden">
      <label htmlFor="dashboard-revise" className="text-[13px] font-medium text-ink">
        What should change?
      </label>
      <Textarea
        id="dashboard-revise"
        autoFocus
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
          if (e.key === 'Escape') onCancel();
        }}
        maxLength={2000}
        disabled={revise.isPending}
        placeholder="For example: add receivables aging, drop the cash forecast, and show 6 months instead of 12."
        className="mt-2 min-h-16 w-full"
      />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12px] text-muted">Uses 1 assistant message. Moving, resizing and adding widgets by hand is free.</p>
        <div className="flex gap-2">
          <Button onClick={onCancel} disabled={revise.isPending}>
            Cancel
          </Button>
          <Button variant="ai" icon={<Icons.Sparkle size={14} />} loading={revise.isPending} disabled={!ready} onClick={submit}>
            {revise.isPending ? 'Updating…' : 'Apply'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function RenameModal({ dashboard, saving, onSave, onClose }: { dashboard: Dashboard; saving: boolean; onSave: (body: { title: string; description: string }) => void; onClose: () => void }) {
  const [title, setTitle] = useState(dashboard.title);
  const [description, setDescription] = useState(dashboard.description);
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename dashboard"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!title.trim()} onClick={() => onSave({ title: title.trim(), description: description.trim() })}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} className="w-full" />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={240} className="w-full" />
        </Field>
      </div>
    </Modal>
  );
}

function RenameWidgetModal({ widget, onSave, onClose }: { widget: Widget; onSave: (title: string) => void; onClose: () => void }) {
  const [title, setTitle] = useState(widget.title);
  const ok = !!title.trim();
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename widget"
      width="max-w-md"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ok} onClick={() => onSave(title.trim())}>
            Save
          </Button>
        </>
      }
    >
      <Field label="Title">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ok && onSave(title.trim())} maxLength={80} className="w-full" />
      </Field>
    </Modal>
  );
}

// --- Adding a widget by hand ---------------------------------------------------------------------------------------

const KIND_GROUPS: { kind: MetricKind; title: string }[] = [
  { kind: 'number', title: 'Headline numbers' },
  { kind: 'series', title: 'Trends over time' },
  { kind: 'breakdown', title: 'Breakdowns' },
  { kind: 'table', title: 'Lists' },
];

/** A widget with the metric's defaults (the server applies the same ones), so it shows at once. */
function newWidget(m: CatalogueMetric): Widget {
  return {
    id: `wdg_${crypto.randomUUID().replace(/-/g, '').slice(0, 24)}`,
    metric: m.key,
    title: m.key === 'spend_breakdown' ? 'Spend by vendor' : m.label,
    view: m.defaultView,
    size: m.kind === 'number' ? 'small' : 'medium',
    ...(m.months ? { months: m.months[0] } : {}),
    ...(m.groupBy ? { groupBy: 'vendor' as const } : {}),
    ...(m.limit ? { limit: m.limit[0] } : {}),
  };
}

function AddWidgetModal({ metrics, onAdd, onClose }: { metrics: CatalogueMetric[]; onAdd: (metric: CatalogueMetric) => void; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = metrics.filter((m) => words.every((w) => `${m.label} ${m.about}`.toLowerCase().includes(w)));
  return (
    <Modal open onClose={onClose} title="Add a widget" width="max-w-2xl">
      <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search: cash, MRR, overdue, vendor…" className="w-full" />
      <p className="mt-2 text-[12px] text-muted">Free: adding by hand uses no assistant message. Change its period, chart or size afterwards from the widget’s menu.</p>
      <div className="mt-3 max-h-[52vh] space-y-4 overflow-y-auto pr-1 scrollbar-thin">
        {KIND_GROUPS.map((g) => {
          const items = shown.filter((m) => m.kind === g.kind);
          if (!items.length) return null;
          return (
            <div key={g.kind}>
              <div className="app-eyebrow mb-1.5 text-[10px] text-faint">{g.title}</div>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {items.map((m) => (
                  <button key={m.key} type="button" onClick={() => onAdd(m)} className="rounded-xl border border-white/80 bg-white/60 px-3 py-2 text-left shadow-card transition-colors hover:border-brand-line hover:bg-white">
                    <div className="text-[13px] font-medium text-ink">{m.label}</div>
                    <div className="mt-0.5 line-clamp-2 text-[12px] text-muted">{m.about}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {!shown.length && <p className="py-6 text-center text-[12.5px] text-muted">Nothing matches “{search}”. RareMargin shows ledger, revenue, spend and cash figures.</p>}
      </div>
    </Modal>
  );
}

// --- Present -------------------------------------------------------------------------------------------------------

/** Full screen, for a board meeting or a wall screen: no controls, figures refresh every minute. Escape leaves. */
function Present({ dashboard, data, onClose }: { dashboard: Dashboard; data?: DashboardData; onClose: () => void }) {
  const { org } = useSession();
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    const el = ref.current;
    let entered = false;
    el?.requestFullscreen?.().then(
      () => (entered = true),
      () => undefined,
    );
    // Leaving full screen (Escape in most browsers) leaves the presentation too.
    const onFullscreen = () => !document.fullscreenElement && entered && close.current();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    document.addEventListener('fullscreenchange', onFullscreen);
    window.addEventListener('keydown', onKey);
    return () => {
      clearInterval(t);
      document.removeEventListener('fullscreenchange', onFullscreen);
      window.removeEventListener('keydown', onKey);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);
  return (
    <div ref={ref} role="dialog" aria-modal aria-label={`Presenting ${dashboard.title}`} className="fixed inset-0 z-[70] overflow-y-auto bg-paper scrollbar-thin">
      <div aria-hidden className="app-backdrop" />
      <div className="relative mx-auto max-w-[1680px] px-8 py-8 lg:px-12">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="app-eyebrow text-muted">
              {org.name} · {now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
            <h1 className="app-title mt-2 text-[40px] leading-[1.02] text-ink">{dashboard.title}</h1>
            {dashboard.description && <p className="mt-2 max-w-3xl text-[15px] text-ink-2">{dashboard.description}</p>}
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 text-[12.5px] text-muted">
              <span className="size-2 animate-pulse rounded-full bg-good" /> Live from the ledger{data && ` · as of ${date(data.asOf)}`}
            </span>
            <Button icon={<Icons.X size={14} />} onClick={onClose}>
              Exit
            </Button>
          </div>
        </header>
        <DashboardGrid dashboard={dashboard} data={data} large />
      </div>
    </div>
  );
}

// --- The grid ------------------------------------------------------------------------------------------------------

/**
 * Four columns once the dashboard is wide enough (a container query, so the screen, present mode and a printed sheet
 * lay out alike), two on tablets, one on phones.
 */
const SPAN: Record<WidgetSize, string> = { small: 'col-span-1', medium: '@2xl:col-span-2', large: '@2xl:col-span-2 @4xl:col-span-4' };

interface Editing {
  metrics: CatalogueMetric[];
  /** The whole new layout; `removed` when a widget was taken out (for Undo). */
  onChange: (widgets: Widget[], removed?: Widget) => void;
}

export function DashboardGrid({ dashboard, data, edit, large }: { dashboard: Dashboard; data?: DashboardData; edit?: Editing; large?: boolean }) {
  const widgets = dashboard.widgets;
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; after: boolean } | null>(null);
  const [renaming, setRenaming] = useState<Widget | null>(null);

  const update = (w: Widget, change: Partial<Widget>) => edit?.onChange(widgets.map((x) => (x.id === w.id ? { ...x, ...change } : x)));
  const moveBy = (w: Widget, by: number) => {
    const i = widgets.indexOf(w);
    const j = i + by;
    if (j < 0 || j >= widgets.length) return;
    const next = [...widgets];
    [next[i], next[j]] = [next[j]!, next[i]!];
    edit?.onChange(next);
  };
  const drop = () => {
    if (drag && over && drag !== over.id) {
      const moving = widgets.find((x) => x.id === drag)!;
      const rest = widgets.filter((x) => x.id !== drag);
      const at = rest.findIndex((x) => x.id === over.id) + (over.after ? 1 : 0);
      const next = [...rest.slice(0, at), moving, ...rest.slice(at)];
      if (next.some((x, i) => x.id !== widgets[i]!.id)) edit?.onChange(next);
    }
    setDrag(null);
    setOver(null);
  };
  const dragOver = (e: DragEvent<HTMLDivElement>, w: Widget) => {
    if (!drag) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const r = e.currentTarget.getBoundingClientRect();
    const after = e.clientX > r.left + r.width / 2;
    if (over?.id !== w.id || over.after !== after) setOver({ id: w.id, after });
  };

  return (
    <div className="@container">
      <div className={cx('grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-4', large ? 'gap-4' : 'gap-3')}>
        {widgets.map((w, i) => (
          <div
            key={w.id}
            data-widget={w.id}
            onDragOver={(e) => dragOver(e, w)}
            onDrop={(e) => (e.preventDefault(), drop())}
            className={cx('relative min-w-0 break-inside-avoid transition-opacity', SPAN[w.size], drag === w.id && 'opacity-40')}
          >
            {over?.id === w.id && drag !== w.id && <span aria-hidden className={cx('pointer-events-none absolute inset-y-2 z-10 w-1 rounded-full bg-brand shadow-[0_0_0_3px_rgb(3_93_68/0.15)]', over.after ? '-right-2' : '-left-2')} />}
            <WidgetCard
              widget={w}
              result={data?.widgets.find((r) => r.id === w.id)}
              currency={data?.currency ?? 'USD'}
              large={large}
              fileName={`${dashboard.title} - ${w.title}`}
              controls={
                edit && {
                  metric: edit.metrics.find((m) => m.key === w.metric),
                  first: i === 0,
                  last: i === widgets.length - 1,
                  only: widgets.length === 1,
                  onChange: (change) => update(w, change),
                  onMove: (by) => moveBy(w, by),
                  onRename: () => setRenaming(w),
                  onRemove: () => edit.onChange(
                    widgets.filter((x) => x.id !== w.id),
                    w,
                  ),
                  onDragStart: (e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', w.id);
                    const card = (e.currentTarget as HTMLElement).closest<HTMLElement>('[data-widget]');
                    if (card) e.dataTransfer.setDragImage(card, 28, 20);
                    setDrag(w.id);
                  },
                  onDragEnd: () => (setDrag(null), setOver(null)),
                }
              }
            />
          </div>
        ))}
      </div>
      {renaming && <RenameWidgetModal widget={renaming} onClose={() => setRenaming(null)} onSave={(title) => (update(renaming, { title }), setRenaming(null))} />}
    </div>
  );
}

/** The dashboard pinned to the Command Center, read-only, with a way into it. */
export function PinnedDashboard() {
  const q = useApi<Dashboard | null>('/api/dashboards/pinned');
  const d = q.data;
  const data = useApi<DashboardData>(d ? `/api/dashboards/${d.id}/data` : null, { refetchInterval: 60_000 });
  if (!d) return null;
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="app-eyebrow flex items-center gap-1.5 text-muted">
            <Icons.Pin size={12} /> Pinned dashboard
          </div>
          <h2 className="app-title mt-1.5 truncate text-[20px] text-ink">{d.title}</h2>
        </div>
        <Link to={`/dashboards/${d.id}`} className="text-[12.5px] font-medium text-brand hover:underline">
          Open dashboard →
        </Link>
      </div>
      <DashboardGrid dashboard={d} data={data.data} />
    </section>
  );
}

// --- Widgets -----------------------------------------------------------------------------------------------------

/** Headline numbers where a rise is bad news. */
const LOWER_IS_BETTER = new Set(['burn', 'opex', 'ar_open', 'ar_overdue', 'ap_open', 'approvals_pending', 'unmatched_cash']);

const VIEW_LABEL: Record<Widget['view'], string> = { number: 'Number', line: 'Line chart', bar: 'Bar chart', bars: 'Bars', table: 'Table' };
const SIZE_LABEL: Record<WidgetSize, string> = { small: 'Small (one column)', medium: 'Medium (two columns)', large: 'Full width' };
const GROUP_LABEL: Record<NonNullable<Widget['groupBy']>, string> = { vendor: 'By vendor', department: 'By department', account: 'By account' };
const PERIODS = [3, 6, 12, 24];

function fmt(format: MetricFormat, v: number | null | undefined, currency: string, compact = true) {
  switch (format) {
    case 'money':
      return money(v, currency, { compact, cents: false });
    case 'percent':
      return pct(v);
    case 'count':
      return num(v);
    case 'months':
      return v === null || v === undefined ? '—' : `${num(v, 1)} mo`;
  }
}

const labelOf = (l: string) => (/^\d{4}-\d{2}$/.test(l) ? period(l, true) : /^\d{4}-\d{2}-\d{2}$/.test(l) ? date(l, { year: false }) : l);

interface Controls {
  metric?: CatalogueMetric;
  first: boolean;
  last: boolean;
  only: boolean;
  onChange: (change: Partial<Widget>) => void;
  onMove: (by: number) => void;
  onRename: () => void;
  onRemove: () => void;
  onDragStart: (e: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

/** The widget's menu: chart, period, grouping, size, position, rename, download, remove. Viewers get the download. */
function widgetMenu(widget: Widget, controls: Controls | undefined, csv: (() => void) | null): MenuEntry[] {
  const out: MenuEntry[] = [];
  const m = controls?.metric;
  if (controls && m) {
    if (m.views.length > 1) {
      out.push({ heading: 'Show as' });
      for (const v of m.views) out.push({ label: VIEW_LABEL[v], checked: widget.view === v, onSelect: () => controls.onChange({ view: v }) });
    }
    if (m.months) {
      const max = m.months[1];
      const options = [...new Set([...PERIODS.filter((p) => p <= max), ...(widget.months ? [widget.months] : [])])].sort((a, b) => a - b);
      out.push({ heading: 'Period' });
      for (const p of options) out.push({ label: `Last ${p} months`, checked: widget.months === p, onSelect: () => controls.onChange({ months: p }) });
    }
    if (m.groupBy && widget.groupBy) {
      out.push({ heading: 'Group' });
      for (const g of m.groupBy as NonNullable<Widget['groupBy']>[]) {
        // A title that was only the default follows the grouping.
        const retitle = widget.title === `Spend by ${widget.groupBy}` ? { title: `Spend by ${g}` } : {};
        out.push({ label: GROUP_LABEL[g], checked: widget.groupBy === g, onSelect: () => controls.onChange({ groupBy: g, ...retitle }) });
      }
    }
  }
  if (controls) {
    out.push({ heading: 'Size' });
    for (const s of ['small', 'medium', 'large'] as const) out.push({ label: SIZE_LABEL[s], checked: widget.size === s, onSelect: () => controls.onChange({ size: s }) });
    out.push('divider');
    out.push({ label: 'Move earlier', icon: <Icons.Chevron size={14} className="rotate-180" />, disabled: controls.first, onSelect: () => controls.onMove(-1) });
    out.push({ label: 'Move later', icon: <Icons.Chevron size={14} />, disabled: controls.last, onSelect: () => controls.onMove(1) });
    out.push({ label: 'Rename widget', icon: <Icons.Doc size={14} />, onSelect: controls.onRename });
  }
  if (csv) out.push({ label: 'Download CSV', icon: <Icons.Download size={14} />, onSelect: csv });
  if (controls && !controls.only) out.push('divider', { label: 'Remove from dashboard', icon: <Icons.Trash size={14} />, danger: true, onSelect: controls.onRemove });
  return out;
}

function WidgetCard({ widget, result, currency, controls, large, fileName }: { widget: Widget; result?: { data?: WidgetData; error?: string }; currency: string; controls?: Controls | false; large?: boolean; fileName: string }) {
  const data = result?.data;
  const ctl = controls || undefined;
  const csv = data ? () => download(`${fileName}.csv`, csvOf(widget, data, currency)) : null;
  const items = widgetMenu(widget, ctl, csv);
  const tools = !large && items.length > 0 && (
    <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 has-[[aria-expanded=true]]:opacity-100 print:hidden [@media(hover:none)]:opacity-100">
      {ctl && (
        <span
          draggable
          onDragStart={ctl.onDragStart}
          onDragEnd={ctl.onDragEnd}
          title="Drag to move"
          aria-hidden
          className="inline-flex h-7 w-6 cursor-grab items-center justify-center rounded-lg text-faint hover:bg-ink/[0.06] hover:text-ink active:cursor-grabbing"
        >
          <Icons.Grip size={15} />
        </span>
      )}
      <Menu label={`${widget.title}: options`} items={items}>
        <Icons.More size={16} />
      </Menu>
    </div>
  );

  if (widget.view === 'number') {
    return (
      <div className={cx('group glass h-full min-w-0 rounded-2xl', large ? 'px-5 py-4' : 'px-4 py-3.5')}>
        <div className="flex min-h-7 items-center justify-between gap-2">
          <div className={cx('truncate font-medium text-muted', large ? 'text-[13.5px]' : 'text-[12px]')}>{widget.title}</div>
          {tools}
        </div>
        {result?.error ? <p className="mt-2 text-[12px] text-bad">{result.error}</p> : data?.kind === 'number' ? <NumberBody metric={widget.metric} data={data} currency={currency} large={large} /> : <Skeleton className="mt-2 h-7 w-28" />}
      </div>
    );
  }

  const isTable = widget.view === 'table' || data?.kind === 'table';
  return (
    <Card title={widget.title} subtitle={widget.months ? `Last ${widget.months} months` : undefined} actions={tools || undefined} pad={!isTable || !data} className="group flex h-full flex-col">
      {result?.error ? (
        <p className="text-[12.5px] text-bad">{result.error}</p>
      ) : !data ? (
        <Skeleton className="h-[200px] w-full" />
      ) : data.kind === 'series' ? (
        <SeriesBody view={widget.view} data={data} currency={currency} large={large} />
      ) : data.kind === 'breakdown' ? (
        <BreakdownBody view={widget.view} data={data} currency={currency} />
      ) : data.kind === 'table' ? (
        <TableBody data={data} currency={currency} />
      ) : null}
    </Card>
  );
}

function NumberBody({ metric, data, currency, large }: { metric: string; data: Extract<WidgetData, { kind: 'number' }>; currency: string; large?: boolean }) {
  const { value, previous, trend, note, format } = data;
  let delta: string | null = null;
  let tone: 'good' | 'bad' | null = null;
  if (value !== null && previous !== null && previous !== undefined && (format === 'percent' || previous !== 0)) {
    // The change as shown (points or a share of last month); one that rounds to 0.0 reads as no change, in neutral grey.
    const change = format === 'percent' ? (value - previous) * 100 : ((value - previous) / previous) * 100;
    if (Math.abs(change) < 0.05) delta = 'No change vs prior month';
    else {
      const up = value > previous;
      delta = `${up ? '+' : '−'}${Math.abs(change).toFixed(1)}${format === 'percent' ? ' pts' : '%'} vs prior month`;
      tone = up !== LOWER_IS_BETTER.has(metric) ? 'good' : 'bad';
    }
  }
  return (
    <>
      <div className={cx('num mt-1 truncate leading-tight font-semibold tracking-[-0.015em] text-ink', large ? 'text-[34px]' : 'text-[23px]')}>{value === null && note ? <span className="text-[16px]">{note}</span> : fmt(format, value, currency)}</div>
      {(delta || (note && value !== null)) && <div className={cx('mt-0.5 truncate', large ? 'text-[13px]' : 'text-[12px]', tone === 'good' ? 'text-good' : tone === 'bad' ? 'text-bad' : 'text-muted')}>{delta ?? note}</div>}
      {trend && trend.length > 1 && <Sparkline values={trend} className="mt-2" />}
    </>
  );
}

function SeriesBody({ view, data, currency, large }: { view: Widget['view']; data: Extract<WidgetData, { kind: 'series' }>; currency: string; large?: boolean }) {
  const labels = data.labels.map(labelOf);
  const full = (v: number) => fmt(data.format, v, currency, false);
  const axis = (v: number) => (data.format === 'percent' ? pct(v, 0) : fmt(data.format, v, currency));
  if (!labels.length) return <p className="text-[12.5px] text-muted">Nothing to show yet.</p>;
  if (view === 'table') {
    return (
      <Table dense className="-mx-4 -my-4">
        <thead>
          <tr>
            <Th>Period</Th>
            {data.series.map((s) => (
              <Th key={s.name} right>
                {s.name}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((l, i) => (
            <Tr key={data.labels[i]}>
              <Td>{l}</Td>
              {data.series.map((s) => (
                <Td key={s.name} right>
                  {full(s.values[i] ?? 0)}
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
    );
  }
  const height = large ? 280 : undefined;
  return (
    <div className="space-y-2">
      {data.series.length > 1 && <Legend series={data.series} />}
      {view === 'bar' ? (
        <BarChart labels={labels} series={data.series} format={full} axisFormat={axis} stacked={data.stacked} height={height} />
      ) : (
        <LineChart labels={labels} series={data.series} format={full} axisFormat={axis} area={data.area} height={height} />
      )}
    </div>
  );
}

function BreakdownBody({ view, data, currency }: { view: Widget['view']; data: Extract<WidgetData, { kind: 'breakdown' }>; currency: string }) {
  if (!data.rows.length || data.rows.every((r) => r.value === 0)) return <p className="text-[12.5px] text-muted">Nothing to show yet.</p>;
  const share = (v: number) => (data.total ? pct(v / data.total, 0) : undefined);
  if (view === 'table') {
    return (
      <Table dense className="-mx-4 -my-4">
        <thead>
          <tr>
            <Th>Name</Th>
            <Th right>Amount</Th>
            <Th right>Share</Th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <Tr key={r.label}>
              <Td>{r.label}</Td>
              <Td right>{fmt(data.format, r.value, currency, false)}</Td>
              <Td right muted>
                {share(r.value) ?? '—'}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    );
  }
  return <HBars rows={data.rows.map((r) => ({ label: r.label, value: r.value, sub: share(r.value) }))} format={(v) => fmt(data.format, v, currency)} />;
}

function TableBody({ data, currency }: { data: Extract<WidgetData, { kind: 'table' }>; currency: string }) {
  const navigate = useNavigate();
  if (!data.rows.length) return <p className="p-4 text-[12.5px] text-muted">Nothing here right now.</p>;
  const right = (f: string) => f === 'money' || f === 'count';
  const cell = (f: string, v: string | number | null | undefined) =>
    v === null || v === undefined ? '—' : f === 'money' ? money(Number(v), currency) : f === 'date' ? date(String(v)) : f === 'count' ? num(Number(v)) : String(v);
  return (
    <>
      <Table dense>
        <thead>
          <tr>
            {data.columns.map((c) => (
              <Th key={c.key} right={right(c.format)}>
                {c.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => (
            <Tr key={r.link ?? i} onClick={r.link ? () => navigate(r.link!) : undefined}>
              {data.columns.map((c) => (
                <Td key={c.key} right={right(c.format)}>
                  {cell(c.format, r.values[c.key])}
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
      {data.total !== undefined && <div className="bg-white/50 px-4 py-2 text-right text-[12px] text-muted">Total {money(data.total, currency)}</div>}
    </>
  );
}

// --- CSV -----------------------------------------------------------------------------------------------------------

/**
 * A widget's figures as CSV: amounts in major units of the base currency (unit in the header), percentages as
 * numbers out of 100. Text that a spreadsheet would run as a formula is quoted as text.
 */
function csvOf(widget: Widget, data: WidgetData, currency: string) {
  const unit = (f: string) => (f === 'money' ? ` (${currency})` : f === 'percent' ? ' (%)' : f === 'months' ? ' (months)' : '');
  const value = (f: string, v: number | null | undefined) =>
    v === null || v === undefined ? '' : f === 'money' ? toMajor(v, currency).toFixed(exponent(currency)) : f === 'percent' ? (v * 100).toFixed(2) : f === 'months' ? v.toFixed(1) : String(v);
  let rows: (string | number | null | undefined)[][];
  switch (data.kind) {
    case 'number':
      rows = [
        ['Metric', `Value${unit(data.format)}`, `Prior month${unit(data.format)}`],
        [widget.title, value(data.format, data.value), value(data.format, data.previous)],
      ];
      break;
    case 'series':
      rows = [['Period', ...data.series.map((s) => s.name + unit(data.format))], ...data.labels.map((l, i) => [l, ...data.series.map((s) => value(data.format, s.values[i]))])];
      break;
    case 'breakdown':
      rows = [['Name', `Amount${unit(data.format)}`, 'Share (%)'], ...data.rows.map((r) => [r.label, value(data.format, r.value), data.total ? ((r.value / data.total) * 100).toFixed(2) : ''])];
      break;
    case 'table':
      rows = [data.columns.map((c) => c.label + (c.format === 'money' ? unit('money') : '')), ...data.rows.map((r) => data.columns.map((c) => (c.format === 'money' ? value('money', r.values[c.key] as number | null) : r.values[c.key])))];
      if (data.total !== undefined) rows.push(data.columns.map((c, i) => (i === 0 ? 'Total' : c.format === 'money' && i === data.columns.length - 1 ? value('money', data.total) : '')));
      break;
  }
  const cell = (v: string | number | null | undefined) => {
    let s = v === null || v === undefined ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n';
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name.replace(/[\\/:*?"<>|]+/g, '').slice(0, 140) });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast.success(`Downloaded ${a.download}`);
}
